import React, { useEffect, useState } from 'react';
import { ICardInfo } from '../../Community/CardInfo/CardInfo';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { faTrophy, faHeart, faUser } from '@fortawesome/free-solid-svg-icons';

import styles from './DesignRow.module.scss';
import ActionButton from '../../ActionButton/ActionButton';
import Spinner from '../../Spinner/spinner';
import { fullPathLogo, startDateString } from '../../../helpers/utils';

import { useNavigate } from 'react-router-dom';
import { Contest, GalleryDesign } from '../../../global';
import DesignPlaceholder from '../../../assets/Design.png';
import { useAuth } from '../../../context/AuthContext';
import { ROUTEPATHS } from '../../../routes/routePaths';
import { userUrlForUser, briefUrlForContest } from '../../../helpers/navigationUtils';

interface Props {
  contest?: Contest;
  design: GalleryDesign;
}

const ContestRowGallery: React.FC<Props> = ({ design, contest }) => {
  const redirect = useNavigate();
  const [images, setImages] = useState<string[]>([]);

  return (
    <>
      {images.map((item, index) => (
        <div className={`${styles.contestRowGalleryItem} ${index % 2 === 0 ? 'bg-light-gray-2' : 'bg-light-gray'}`}>
          {item ? <img src={fullPathLogo(item)} className="cursor-pointer" alt="Gallery Item Cover" /> : <Spinner size={5} />}
        </div>
      ))}
    </>
  );
};

const DesignRow: React.FC<Props> = ({ contest, design }) => {
  const redirect = useNavigate();
  const { loggedUser: user, isDesignLiked, toggleDesignLiked } = useAuth();
  const [imagesPath, setImagesPath] = useState<string[]>(['']);
  const [designName, setDesignName] = useState<string>('');

  useEffect(() => {
    if (design) {
      if (design.name) {
        setDesignName(design.name);
      } else {
        if (design.isPrivate || design.hideGalleryTab) {
          setDesignName('Private gallery');
        } else if (design.isHidden) {
          setDesignName('Hidden by author');
        }
      }
    }
  }, [design]);

  const goToDesignDetails = async () => {
    if (!design.isHidden && !design.isPrivate && !design.hideGalleryTab) {
      redirect(`/${ROUTEPATHS.CONTEST}/${design.contestNameForURL}/${ROUTEPATHS.CONTEST_GALLERY}/${design.nameForURL}`);
    }
  };

  const goToUserDetail = async (nameForURL: string) => {
    redirect(userUrlForUser(nameForURL));
  };

  const goToContestDetails = async (contestNameForURL: string) => {
    redirect(briefUrlForContest(contestNameForURL));
  };

  return (
    <div className={`${styles.contestRowWrapper} col-12 mb-5 position-relative`}>
      <div className="row bg-light">
        <div className="col-12 col-lg-7 d-flex flex-nowrap px-0">
          {/* Cover Image */}
          <div
            className={`${design.image ? styles.contestRowImage : styles.contestRowImagePlaceholder} bg-light-gray-2 flex-shrink-0 position-relative 
            ${!design.isHidden && !design.isPrivate && !design.hideGalleryTab ? 'cursor-pointer' : ''}`}
          >
            {design.image ? (
              <img src={design.image ?? DesignPlaceholder} alt="Contest Cover" onClick={() => goToDesignDetails()} />
            ) : // <FontAwesomeIcon icon={faPencil as IconProp} className="color-light" />
            null}
            {!isDesignLiked(design) && (
              <ActionButton color={!user ? 'light-gray-3' : ''} icon="addFavorite" position="top-right" action={() => user && toggleDesignLiked(design)} />
            )}
            {isDesignLiked(design) && <ActionButton icon="confirm" position="top-right" color="success" action={() => toggleDesignLiked(design)} />}
            {design.isPrivate && <ActionButton icon="private" label="Private gallery" position="bottom-right" color="" />}
            {design.hasAwards && <ActionButton label="award" icon="award" />}
            {design.isHidden && <ActionButton label="Hidden by author" icon="private" />}
          </div>
          {/* Project Info */}
          <div className="flex-grow-1">
            <div className="row ps-0 flex-grow-1 h-100">
              {/* Project Title */}
              <div className="col-12  col-lg-5 d-flex flex-wrap  ps-5 mt-4 mt-lg-0" style={{ height: '90px' }}>
                <div className="d-flex align-items-center mt-2" onClick={() => goToDesignDetails()}>
                  <h3 className={`text-xl d-flex fw-bold color-blue ${!design.isHidden && !design.isPrivate && !design.hideGalleryTab ? 'cursor-pointer' : ''}`}>{designName}</h3>
                </div>
              </div>
              {/* Project Author */}
              <div className="col-6 col-lg-4 d-flex flex-column justify-content-center ps-5">
                <div className="d-flex justify-content-start">
                  <div className="d-flex flex-column justify-content-center">
                    <FontAwesomeIcon icon={faUser as IconProp} className=" text color-blue me-3" />
                  </div>
                  <div className="text-s text-start color-blue cursor-pointer" onClick={() => goToUserDetail(design.userNameForURL)}>
                    {design.username}
                  </div>
                </div>
              </div>
              {/* Project Stats */}
              <div className="col-6 col-lg-3 d-flex flex-column justify-content-center px-2 ">
                <div className="d-flex justify-content-start mb-2 ">
                  <div className="d-flex ms-1 justify-content-center align-items-center">
                    <span>
                      <FontAwesomeIcon icon={faCalendar as IconProp} className=" text color-blue me-3" />
                    </span>
                  </div>
                  <div className="text-s text-start ">Posted: {startDateString(design.uploadDate)}</div>
                </div>
                <div className="d-flex justify-content-start mb-2">
                  <div className="d-flex justify-content-center align-items-center ">
                    <span>
                      <FontAwesomeIcon icon={faTrophy as IconProp} className=" text color-blue me-3" />
                    </span>
                  </div>
                  <div className="text-s text-start color-blue cursor-pointer" onClick={() => goToContestDetails(design.contestNameForURL)}>
                    {design.contestName}
                  </div>
                </div>
                <div className="d-flex justify-content-start mb-2 ">
                  <div className=" ms-1 d-flex justify-content-center align-items-center ">
                    <span>
                      <FontAwesomeIcon icon={faHeart as IconProp} className=" text color-blue me-3" />
                    </span>
                  </div>
                  <div className="text-s text-start ">{design.votesCount} votes</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Project Gallery */}
        <div className={`${styles.contestRowGallery}  col-12 col-lg-5 d-flex justify-content-center justify-content-lg-end px-0 mt-3 mt-lg-0`}>
          <ContestRowGallery design={design} contest={contest} />
        </div>
      </div>
    </div>
  );
};

export default DesignRow;
